/**
 * DtcList.tsx — Attached DTC codes list for a work order.
 *
 * Shows each attached DTC with a status pill (detected / diagnosing /
 * resolved / dismissed).  Tech can change status or remove a code.
 *
 * Feature-gated: rendered only when the shop has DTC diagnosis enabled (useAiFeatureFlags, migration 195 — owner-controlled in AI settings).
 */

import React, { useEffect } from 'react';
import {
  Card,
  List,
  Tag,
  Select,
  Button,
  Spin,
  Alert,
  Space,
  Typography,
  Popconfirm,
} from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { useDtc } from '../../hooks/useDtc';
import type { AttachedDtc, DtcStatus } from '../../services/dtcService';

const { Text } = Typography;

const STATUS_COLOR: Record<DtcStatus, string> = {
  detected: 'orange',
  diagnosing: 'blue',
  resolved: 'green',
  dismissed: 'default',
};

const STATUS_OPTIONS: Array<{ value: DtcStatus; label: string }> = [
  { value: 'detected', label: 'Detected' },
  { value: 'diagnosing', label: 'Diagnosing' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'dismissed', label: 'Dismissed' },
];

export interface DtcListProps {
  workOrderId: number;
  /** Pass this prop to trigger a list reload after an attach event in DtcLookupCard */
  refreshTrigger?: number;
}

const DtcList: React.FC<DtcListProps> = ({ workOrderId, refreshTrigger }) => {
  const { dtcList, loadList, setStatus, removeDtc } = useDtc();

  useEffect(() => {
    loadList(workOrderId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workOrderId, refreshTrigger]);

  const items = dtcList.data ?? [];

  return (
    <Card
      size="small"
      title={
        <Space>
          <Text strong>Attached DTCs</Text>
          {items.length > 0 && (
            <Tag>{items.length}</Tag>
          )}
        </Space>
      }
      style={{ marginTop: 8 }}
      data-testid="dtc-list-card"
    >
      {dtcList.loading && (
        <Spin style={{ display: 'block', margin: '16px auto' }} />
      )}

      {dtcList.error && (
        <Alert type="error" message={dtcList.error} style={{ marginBottom: 8 }} />
      )}

      {!dtcList.loading && items.length === 0 && (
        <Text type="secondary" data-testid="dtc-list-empty">
          No DTCs attached yet.
        </Text>
      )}

      {!dtcList.loading && items.length > 0 && (
        <List
          dataSource={items}
          size="small"
          renderItem={(dtc: AttachedDtc) => (
            <List.Item
              key={dtc.id}
              data-testid={`dtc-list-item-${dtc.code}`}
              extra={
                <Popconfirm
                  title="Remove this DTC?"
                  onConfirm={() => removeDtc(workOrderId, dtc.id)}
                  okText="Remove"
                  cancelText="Cancel"
                >
                  <Button
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    data-testid={`dtc-remove-btn-${dtc.code}`}
                  />
                </Popconfirm>
              }
            >
              <List.Item.Meta
                title={
                  <Space wrap>
                    <Text strong>{dtc.code}</Text>
                    <Tag color={STATUS_COLOR[dtc.status]} data-testid={`dtc-status-tag-${dtc.code}`}>
                      {dtc.status}
                    </Tag>
                    {dtc.system && (
                      <Tag color="purple">{dtc.system}</Tag>
                    )}
                  </Space>
                }
                description={
                  <Space direction="vertical" size={2} style={{ width: '100%' }}>
                    {dtc.definition && (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {dtc.definition}
                      </Text>
                    )}
                    <Select
                      size="small"
                      value={dtc.status}
                      options={STATUS_OPTIONS}
                      onChange={(val: DtcStatus) => setStatus(workOrderId, dtc.id, val)}
                      style={{ width: 140 }}
                      data-testid={`dtc-status-select-${dtc.code}`}
                    />
                  </Space>
                }
              />
            </List.Item>
          )}
        />
      )}
    </Card>
  );
};

export default DtcList;
